import { Suspense } from 'react';

import { Box, Container, Stack } from '@iwoca/orion';
import { maintainStateKey } from '@iwRouter/LocationHelper';
import { Redirect, Route, Switch, useLocation } from 'react-router-dom';

import { BackToListLink } from './components/BackToListLink/BackToListLink';
import { FundingProgressIndicator } from './components/FundingProgressIndicator/FundingProgressIndicator';
import { FundingRequirementsPageLoading } from './components/FundingRequirementsPageLoading/FundingRequirementsPageLoading';
import { FundingRequirementsShell } from './components/FundingRequirementsShell/FundingRequirementsShell';
import { DesktopLoanSummary } from './components/LoanSummary/DesktopLoanSummary';
import { MobileLoanSummary } from './components/LoanSummary/MobileLoanSummary';
import { RequirementsHeader } from './components/RequirementsHeader/RequirementsHeader';
import { ApplicantCallPage } from './components/RequirementsList/requirements/ApplicantCall/ApplicantCallPage';
import { BankDetailsPage } from './components/RequirementsList/requirements/BankDetails/BankDetailsPage';
import { CreditAgreementPage } from './components/RequirementsList/requirements/CreditAgreement/CreditAgreementPage';
import { IdVerificationPage } from './components/RequirementsList/requirements/IdVerification/IdVerificationPage';
import { PersonalGuaranteePage } from './components/RequirementsList/requirements/PersonalGuarantee/PersonalGuaranteePage';
import { UploadBankEvidencePage } from './components/RequirementsList/requirements/UploadBankEvidence/UploadBankEvidencePage';
import { UploadGewerbescheinPage } from './components/RequirementsList/requirements/UploadGewerbeschein/UploadGewerbescheinPage';
import { RequirementsList } from './components/RequirementsList/RequirementsList';
import { useFundingRequirementsParams } from './hooks/useFundingRequirementsParams';
import { useGetFormalOffer } from './hooks/useGetFormalOffer';
import {
    FUNDING_REQUIREMENTS_LIST_PATH,
    REQUIREMENT_STEP_PATHS
} from './requirementStepPaths';
import { ErrorBoundary } from '../../../../../../errorBoundary/ErrorBoundary';
import FallbackErrorModal from '../../../../../modals/fallbackErrorModal/FallbackErrorModal';

export const FundingRequirementsPage = () => {
    return (
        <ErrorBoundary fallback={() => <FallbackErrorModal />}>
            <Suspense fallback={<FundingRequirementsPageLoading />}>
                <FundingRequirementsPageContent />
            </Suspense>
        </ErrorBoundary>
    );
};

const FundingRequirementsPageContent = () => {
    const { offerId, drawdownAmount } = useFundingRequirementsParams();
    const formalOffer = useGetFormalOffer(offerId);
    const { search } = useLocation();

    return (
        <FundingRequirementsShell
            sidePanel={
                <DesktopLoanSummary
                    formalOffer={formalOffer}
                    drawdownAmount={drawdownAmount}
                />
            }
            progressIndicator={<FundingProgressIndicator />}
            mainContent={
                <>
                    <Box hideFrom="desktop">
                        <MobileLoanSummary
                            formalOffer={formalOffer}
                            drawdownAmount={drawdownAmount}
                        />
                    </Box>
                    <Container size="focus" paddingY="container">
                        <Switch>
                            <Route exact path={FUNDING_REQUIREMENTS_LIST_PATH}>
                                <Stack gap="comfortable">
                                    <RequirementsHeader />
                                    <RequirementsList />
                                </Stack>
                            </Route>
                            <Route path={Object.values(REQUIREMENT_STEP_PATHS)}>
                                <Stack>
                                    <BackToListLink />
                                    <StepRoutes />
                                </Stack>
                            </Route>
                            <Route>
                                <Redirect
                                    to={maintainStateKey({
                                        pathname:
                                            FUNDING_REQUIREMENTS_LIST_PATH,
                                        search
                                    })}
                                />
                            </Route>
                        </Switch>
                    </Container>
                </>
            }
        />
    );
};

const StepRoutes = () => (
    <Switch>
        <Route path={REQUIREMENT_STEP_PATHS.bank_details}>
            <BankDetailsPage />
        </Route>
        <Route path={REQUIREMENT_STEP_PATHS.id_verification}>
            <IdVerificationPage />
        </Route>
        <Route path={REQUIREMENT_STEP_PATHS.upload_gewerbeschein}>
            <UploadGewerbescheinPage />
        </Route>
        <Route path={REQUIREMENT_STEP_PATHS.credit_agreement}>
            <CreditAgreementPage />
        </Route>
        <Route path={REQUIREMENT_STEP_PATHS.personal_guarantee}>
            <PersonalGuaranteePage />
        </Route>
        <Route path={REQUIREMENT_STEP_PATHS.upload_bank_evidence}>
            <UploadBankEvidencePage />
        </Route>
        <Route path={REQUIREMENT_STEP_PATHS.applicant_call}>
            <ApplicantCallPage />
        </Route>
    </Switch>
);
